// Service to fetch users from API
// Minimal API client: only Basic Auth in prod, always /api/platformUsers in dev

const isDev = process.env.NODE_ENV === 'development';
const BASE = isDev ? '' : (process.env.REACT_APP_API_BASE_URL || '');
const USERS_ENDPOINT = BASE ? `${BASE.replace(/\/$/, '')}/api/platformUsers` : '/api/platformUsers';

function getBasicAuthHeader() {
    // Prefer env, fallback to localStorage
    const user = process.env.REACT_APP_API_USER ||
        (typeof window !== 'undefined' && window.localStorage.getItem('REACT_APP_API_USER'));
    const pass = process.env.REACT_APP_API_PASSWORD ||
        (typeof window !== 'undefined' && window.localStorage.getItem('REACT_APP_API_PASSWORD'));

    if (user && pass) {
        let encoded;
        if (typeof window !== 'undefined' && window.btoa) {
            encoded = window.btoa(`${user}:${pass}`);
        } else if (typeof Buffer !== 'undefined') {
            encoded = Buffer.from(`${user}:${pass}`).toString('base64');
        }
        return `Basic ${encoded}`;
    }
    return undefined;
}

const getHeaders = () => {
    const headers = { 'Content-Type': 'application/json' };
    // Always include Basic Auth for Spring Security
    const auth = getBasicAuthHeader();
    if (auth) headers['Authorization'] = auth;
    return headers;
};

// Helper to map frontend fields to backend fields (mostly 1:1 here but good for safety)
const mapToBackend = (frontendUser) => {
    const backendUser = {};
    if (frontendUser.firstName !== undefined) backendUser.firstName = frontendUser.firstName;
    if (frontendUser.lastName !== undefined) backendUser.lastName = frontendUser.lastName;
    if (frontendUser.phoneNumber !== undefined) backendUser.phoneNumber = frontendUser.phoneNumber;
    if (frontendUser.license !== undefined) backendUser.license = frontendUser.license;
    if (frontendUser.endPoint !== undefined) backendUser.endPoint = frontendUser.endPoint;
    if (frontendUser.essenNumber !== undefined) backendUser.essenNumber = parseInt(frontendUser.essenNumber) || 0;
    if (frontendUser.customLogo !== undefined) backendUser.customLogo = frontendUser.customLogo;
    if (frontendUser.accentColor !== undefined) backendUser.accentColor = frontendUser.accentColor;
    if (frontendUser.subscriptionExpirationDate !== undefined) backendUser.subscriptionExpirationDate = frontendUser.subscriptionExpirationDate;

    return backendUser;
};

export async function fetchUsers() {
    const res = await fetch(USERS_ENDPOINT, { headers: getHeaders() });
    if (!res.ok) throw new Error(`Request failed with status ${res.status}`);

    const data = await res.json();
    let users = [];
    if (Array.isArray(data)) users = data;
    else if (data && Array.isArray(data.users)) users = data.users;
    else if (data && Array.isArray(data.data)) users = data.data;
    else throw new Error('API response is not an array');

    return users.map(user => ({
        id: user.id,
        firstName: user.firstName,
        lastName: user.lastName,
        phoneNumber: user.phoneNumber,
        license: user.license,
        endPoint: user.endPoint,
        essenNumber: user.essenNumber,
        customLogo: user.customLogo,
        accentColor: user.accentColor,
        interactions: user.interactions,
        subscriptionExpirationDate: user.subscriptionExpirationDate
    }));
}

export async function createUser(user) {
    const backendUser = mapToBackend(user);
    const res = await fetch(USERS_ENDPOINT, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify(backendUser)
    });
    if (!res.ok) throw new Error(`Failed to create user: ${res.status}`);
    return true;
}

export async function updateUser(id, updates) {
    const backendUpdates = mapToBackend(updates);
    // Ensure ID is sent in body if required (following items pattern)
    backendUpdates.id = id;

    const res = await fetch(`${USERS_ENDPOINT}/${id}`, {
        method: 'PUT',
        headers: getHeaders(),
        body: JSON.stringify(backendUpdates)
    });
    if (!res.ok) throw new Error(`Failed to update user: ${res.status}`);
    return true;
}

export async function deleteUser(id) {
    const res = await fetch(`${USERS_ENDPOINT}/${id}`, {
        method: 'DELETE',
        headers: getHeaders()
    });
    if (!res.ok) throw new Error(`Failed to delete user: ${res.status}`);
    return true;
}

export async function renewUserSubscription(id) {
    const res = await fetch(`${USERS_ENDPOINT}/${id}/renew`, {
        method: 'POST',
        headers: getHeaders()
    });
    if (!res.ok) throw new Error(`Failed to renew user subscription: ${res.status}`);
    return true;
}

export async function getUserById(id) {
    const apiBase = BASE ? BASE.replace(/\/$/, '') : '';
    const res = await fetch(`${USERS_ENDPOINT}/${id}`, { headers: getHeaders() });
    if (!res.ok) throw new Error(`User ${id} not found`);
    return await res.json();
}

export async function getUserByEssenNumber(essenNumber) {
    const apiBase = BASE ? BASE.replace(/\/$/, '') : '';
    // Idealmente este endpoint es público en el backend y no requiere Basic Auth
    const res = await fetch(`${apiBase}/api/public/platformUsers/${essenNumber}`, {
        headers: getHeaders()
    });

    if (!res.ok) {
        console.warn(`No se pudo encontrar el usuario con número Essen: ${essenNumber}`);
        return null;
    }

    const data = await res.json();
    return {
        phoneNumber: data.phoneNumber,
        customLogo: data.customLogo,
        accentColor: data.accentColor,
        essenNumber: data.essenNumber,
        interactions: data.interactions,
        license: data.license
    };
}

export async function incrementUserInteractions(essenNumber) {
    const apiBase = BASE ? BASE.replace(/\/$/, '') : '';
    const res = await fetch(`${apiBase}/api/public/platformUsers/${essenNumber}/interaction`, {
        method: 'POST',
        headers: getHeaders()
    });

    if (!res.ok) {
        console.warn(`No se pudo incrementar las interacciones del usuario: ${essenNumber}`);
        return false;
    }

    return true;
}

export async function checkUserExists(id) {
    const apiBase = BASE ? BASE.replace(/\/$/, '') : '';
    const res = await fetch(`${apiBase}/api/userExist/${id}`, { headers: getHeaders() });
    if (!res.ok) return false;
    return await res.json();
}

export default fetchUsers;
